/**
 * Databricks-backed occupation (job-title) fetch — source of truth for
 * the Workforce Add User occupation dropdown.
 *
 * Cloud Lead + Samuel 2026-06-17: occupations are JOB TITLES (e.g.
 * "Haul Truck Operator"), pulled from `employee_roster.job_title`.
 * They are distinct from orientations, which are training-session
 * display names served by the MineTwin module (`visible_orientations`).
 * `user.occupation` (string column) is compared against
 * `Orientation.occupation_target: Vec<String>` for both client-side
 * auto-select in Orientation Assignment and server-side `try_auto_assign`.
 *
 * Architecture: the portal calls a backend proxy at
 * `/api/databricks-sql` (Azure Function in api/src/functions/databricks-sql.ts).
 * The Function authenticates to Databricks via Service Principal — SP
 * credentials live as SWA app settings, never in the client bundle. The
 * Function exists primarily to solve the prod CORS problem: Databricks
 * doesn't set CORS headers for the SWA origin, so portal-direct calls
 * to `/oidc/v1/token` and `/api/2.0/sql/statements` failed preflight.
 *
 * What lives where now:
 *   - Catalog resolution     → here (ORG_CATALOG_MAP, keyed by WorkOS
 *                              organizationId)
 *   - Schema / table names   → here (env vars)
 *   - SQL statement          → lives server-side as the named query
 *                              `job_titles`; we send {query, catalog, schema}
 *   - Databricks host/SP/etc → Azure Function env (process.env.*)
 *
 * Failure modes:
 *  - WorkOS user not yet resolved → returns isReady=false until AuthKit
 *    populates `user` (so we know we're in a signed-in session before
 *    posting to the Function, which requires the SWA principal header).
 *  - Function returns 5xx → `error` carries the Databricks message
 *    verbatim. UX (OccupationSelect) surfaces it beneath the select.
 *  - Function returns 200 with empty `data_array` → dropdown is empty
 *    with the "No occupations…" hint.
 *  - Local dev without `func start` → Vite proxy returns 503;
 *    OccupationSelect shows the "API not running" hint.
 */

import { useEffect, useState } from 'react';
import { useAuthKit } from '@/contexts/AuthProvider';

/**
 * `title` carries the Databricks `job_title` column verbatim (e.g.
 * "Haul Truck Operator"). Field name kept as `title` for back-compat
 * with `useOccupations` consumers; semantically it is a job title now,
 * not an orientation display_name.
 */
export type TaxonomyRow = { id: string; title: string };

// ---------------------------------------------------------------------------
// Caching layer — pinned to (catalog, schema, table) so multiple
// concurrent consumers (e.g. Workforce body + the OccupationSelect
// inside the Add User drawer) share one network round-trip.
//
// Three tiers, fall through in order:
//   1. `MEMORY_CACHE` — in-process Map; instant; lost on page reload.
//   2. `sessionStorage` (`STORAGE_PREFIX`) — instant; survives reload
//      within the same browser tab.
//   3. `IN_FLIGHT` — if a fetch for the same key is already running,
//      reuse its Promise instead of firing a duplicate.
//
// TTL is 10 minutes — occupations are a Databricks reference table
// that changes rarely (Stefan refreshes weekly at most), so a stale
// dropdown for a coordinator who's been on the page > 10 min is fine.
// We always revalidate in the background after serving cache so the
// next mount sees fresh data; this is plain stale-while-revalidate.
// ---------------------------------------------------------------------------
type CacheEntry = { rows: TaxonomyRow[]; ts: number };
const MEMORY_CACHE = new Map<string, CacheEntry>();
const IN_FLIGHT = new Map<string, Promise<TaxonomyRow[]>>();
const CACHE_TTL_MS = 10 * 60 * 1000;
const STORAGE_PREFIX = 'mtwin:db-taxonomy:';

function readSessionCache(key: string): CacheEntry | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_PREFIX + key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CacheEntry;
    if (!parsed || !Array.isArray(parsed.rows) || typeof parsed.ts !== 'number') {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function writeSessionCache(key: string, entry: CacheEntry): void {
  try {
    sessionStorage.setItem(STORAGE_PREFIX + key, JSON.stringify(entry));
  } catch {
    // QuotaExceeded / private mode — silently skip; the memory cache
    // still serves this session.
  }
}

const CATALOG_OVERRIDE = import.meta.env.VITE_DATABRICKS_CATALOG as
  | string
  | undefined;
const DEFAULT_SCHEMA =
  (import.meta.env.VITE_DATABRICKS_SCHEMA as string | undefined) || 'reference';
const TABLE =
  (import.meta.env.VITE_DATABRICKS_TAXONOMY_TABLE as string | undefined) ||
  'employee_roster';

// WorkOS org_id → Databricks catalog name. The "minetwin" house org gets
// the special-cased catalog name `minetwin_practice` (per the original
// Plans/04-finished/DATABRICKS-ORIENTATIONS-PLAN.md), NOT the templated
// `minetwin_<slug>_<env>` form. Future customer orgs follow the templated
// form (e.g. `minetwin_acme_copper_prd`) and get added here when they're
// provisioned in Databricks.
//
// Adding a new org:
//   1. Stefan/data team provisions `minetwin_<slug>_<env>.reference.employee_roster`
//      in the Databricks workspace with at least a `job_title` column.
//   2. Add an entry below mapping their WorkOS org_id → that catalog name.
//   3. Ship — no other code changes needed.
const ORG_CATALOG_MAP: Record<string, string> = {
  // minetwin (house org). cloud.lead@arvikasoft.se and the rest of the
  // Arvikasoft / VectorFoundries team sign in under this org.
  org_01KWJF5AT8N0YRT219MRH1FM6T: 'minetwin_practice',
};

/**
 * Resolve which Databricks catalog the signed-in user's employee roster
 * lives in. Order of precedence:
 *  1. `ORG_CATALOG_MAP[organizationId]` — the production answer.
 *  2. `VITE_DATABRICKS_CATALOG` env override — useful in local dev OR
 *     fallback for unmapped orgs.
 *  3. Synthetic placeholder `minetwin_<orgId>_unmapped` — surfaces the
 *     missing entry as a "table not found" error in the UI.
 */
function resolveCatalog(organizationId: string | null | undefined): string {
  if (organizationId && ORG_CATALOG_MAP[organizationId]) {
    return ORG_CATALOG_MAP[organizationId];
  }
  if (CATALOG_OVERRIDE) return CATALOG_OVERRIDE;
  return `minetwin_${organizationId ?? 'unknown'}_unmapped`;
}

type DatabricksSqlResponse = {
  status?: 'ok';
  data_array?: unknown[][];
  error?: string;
};

export function useDatabricksTaxonomy(
  catalog?: string,
  schema: string = DEFAULT_SCHEMA,
): {
  rows: TaxonomyRow[];
  isReady: boolean;
  error: string | null;
} {
  const { user, organizationId, getAccessToken } = useAuthKit();
  const effectiveCatalog = catalog ?? resolveCatalog(organizationId);
  const cacheKey = `${effectiveCatalog}:${schema}:${TABLE}`;
  // Seed from any available cache so the dropdown renders instantly
  // when the consumer mounts inside a session that has already paid
  // the warehouse cold-start tax. The background fetch below
  // revalidates regardless, so coordinators always see fresh data on
  // the next mount.
  const [rows, setRows] = useState<TaxonomyRow[]>(() => {
    const mem = MEMORY_CACHE.get(cacheKey);
    if (mem && Date.now() - mem.ts < CACHE_TTL_MS) return mem.rows;
    const ss = readSessionCache(cacheKey);
    if (ss && Date.now() - ss.ts < CACHE_TTL_MS) {
      MEMORY_CACHE.set(cacheKey, ss);
      return ss.rows;
    }
    return [];
  });
  const [isReady, setIsReady] = useState<boolean>(() => rows.length > 0);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    // If we already served fresh cache above, keep isReady=true so
    // the dropdown stays interactive while we revalidate. If the
    // (catalog, schema) tuple changed since last mount, drop to
    // loading until the new fetch lands.
    const haveFresh = (() => {
      const mem = MEMORY_CACHE.get(cacheKey);
      if (mem && Date.now() - mem.ts < CACHE_TTL_MS) {
        if (rows !== mem.rows) setRows(mem.rows);
        return true;
      }
      return false;
    })();
    if (!haveFresh) {
      setIsReady(false);
    }

    async function fireFetch(workosJwt: string): Promise<TaxonomyRow[]> {
      // DISTINCT — `employee_roster` carries one row per employee, so
      // `job_title` repeats heavily across the workforce; we want each
      // unique job title once for the occupation dropdown. IS NOT NULL
      // guard keeps blank rows out.
      // SQL now lives server-side as the named query `job_titles`.
      // X-Workos-Authorization not Authorization: SWA overwrites the
      // standard header with its own SCM JWT before forwarding to the
      // Function (confirmed 2026-06-15). Custom headers pass through.
      const res = await fetch('/api/databricks-sql', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Workos-Authorization': `Bearer ${workosJwt}`,
        },
        body: JSON.stringify({
          query: 'job_titles',
          catalog: effectiveCatalog,
          schema,
        }),
      });
      if (res.status === 404 || res.status === 503) {
        throw new Error(
          import.meta.env.DEV
            ? 'API not running locally (start `func start` in api/ to enable the catalog fetch)'
            : 'Databricks proxy API endpoint not reachable (deploy may be incomplete)',
        );
      }
      const body = (await res.json().catch(() => ({}))) as DatabricksSqlResponse;
      if (!res.ok || body.error) {
        throw new Error(body.error ?? `HTTP ${res.status}`);
      }
      const dataArr = body.data_array ?? [];
      return dataArr.map((row) => {
        const title = String(row[0] ?? '');
        return { id: title, title };
      });
    }

    async function run() {
      if (!user) {
        // AuthKit hasn't resolved yet — stay in loading state. The
        // user-effect re-fires when `user` populates. The Function
        // also requires the WorkOS JWT as a Bearer header (SWA
        // doesn't inject x-ms-client-principal for WorkOS-authed
        // sessions), so posting before sign-in would 401 anyway.
        return;
      }
      try {
        // Dedup concurrent fetches across hook instances (e.g. the
        // Workforce body prefetch + the OccupationSelect inside the
        // Add User drawer both mounting in the same render pass).
        const inFlight = IN_FLIGHT.get(cacheKey);
        const fetched = await (inFlight
          ?? (async () => {
            const workosJwt = await getAccessToken();
            const p = fireFetch(workosJwt);
            IN_FLIGHT.set(cacheKey, p);
            try {
              return await p;
            } finally {
              IN_FLIGHT.delete(cacheKey);
            }
          })());
        if (cancelled) return;
        const entry: CacheEntry = { rows: fetched, ts: Date.now() };
        MEMORY_CACHE.set(cacheKey, entry);
        writeSessionCache(cacheKey, entry);
        setRows(fetched);
        setIsReady(true);
      } catch (err: unknown) {
        if (cancelled) return;
        const msg = err instanceof Error ? err.message : String(err);
        setError(msg);
        setIsReady(true);
      }
    }

    void run();
    return () => {
      cancelled = true;
    };
    // `rows` is intentionally excluded so we don't refetch every time
    // we publish a new array — the cache is the dedup mechanism.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cacheKey, user, getAccessToken]);

  return { rows, isReady, error };
}
